<script>
  import {
    Popup,
    Page,
    Navbar,
    NavRight,
    Link,
    Block,
    Card,
    CardHeader,
    CardContent,
    Row,
    Col,
    Badge,
    Button,
    Icon,
    f7,
  } from "framework7-svelte";
  import {
    parsePayload,
    formatFullTimestamp as formatTimestamp,
    getEventBadgeColor,
    safeNumber,
  } from "../js/audit-utils";

  export let show = false;
  export let event = null;
  export let onClose = () => {};

  let copySuccess = false;

  $: payload = parsePayload(event?.payload_json);
  $: techSnapshot = payload.technical_snapshot || (event?.technical_snapshot || null);
  $: riskSnapshot = payload.risk_snapshot || (event?.risk_snapshot || null);
  $: latency = payload.latency_metrics || (event?.latency_metrics || null);

  function copyJson() {
    if (!event) return;
    const jsonStr = JSON.stringify(event, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(jsonStr)
        .then(() => {
          copySuccess = true;
          setTimeout(() => {
            copySuccess = false;
          }, 2000);
          f7.toast
            .create({
              text: "✓ Forensic event JSON copied to clipboard",
              closeTimeout: 2000,
            })
            .open();
        })
        .catch((err) => {
          console.warn("Clipboard copy error:", err);
        });
    }
  }
</script>

<Popup opened={show} onPopupClosed={onClose} class="audit-decision-popup">
  <Page style="background: #0f172a; color: #e2e8f0;">
    <Navbar title="Forensic Decision Card: {event?.event_type || 'Audit Record'}">
      <NavRight>
        <Link popupClose iconF7="xmark_circle_fill" />
      </NavRight>
    </Navbar>

  {#if event}
    <div class="decision-card-container">
      <!-- Top Overview Bar -->
      <div class="decision-hero-header">
        <div class="hero-left">
          <div class="hero-badge-row">
            <span class="event-badge badge-{getEventBadgeColor(event.event_type)}">
              {event.event_type}
            </span>
            <span class="env-badge badge-live">
              LIVE
            </span>
            <span class="source-badge">
              {event.source || 'ENGINE_AUTONOMOUS'}
            </span>
          </div>
          <div class="hero-id-row">
            <span class="hero-event-id"><code>{event.event_id || `EVT_${event.id}`}</code></span>
            <span class="hero-time">⏱ {formatTimestamp(event.created_at || event.timestamp)}</span>
          </div>
        </div>
        <div class="hero-right">
          <Button small fill color="blue" onClick={copyJson}>
            <Icon f7="doc_on_clipboard" size="14" /> &nbsp; {copySuccess ? 'Copied!' : 'Copy Full JSON'}
          </Button>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="decision-grid">
        <!-- Section 1: "Why Did It Trade?" / Technical Snapshot -->
        <div class="grid-section">
          <div class="section-title">
            <span>📈 Strategy & Technical Condition</span>
            {#if event.strategy_id}
              <span class="strategy-tag">{event.strategy_id}</span>
            {/if}
          </div>
          <div class="section-card">
            {#if event.trigger_reason}
              <div class="trigger-reason-box">
                <span class="trigger-label">Trigger Condition Met:</span>
                <span class="trigger-text">{event.trigger_reason}</span>
              </div>
            {/if}

            {#if techSnapshot}
              <div class="metrics-grid">
                <div class="metric-pill">
                  <span class="pill-k">LTP at Signal</span>
                  <span class="pill-v text-blue">₹{techSnapshot.ltp != null ? Number(techSnapshot.ltp).toFixed(2) : '-'}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">VWAP</span>
                  <span class="pill-v">₹{techSnapshot.vwap != null ? Number(techSnapshot.vwap).toFixed(2) : '-'}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">RSI (14)</span>
                  <span class="pill-v {Number(techSnapshot.rsi_14) > 70 ? 'text-red' : Number(techSnapshot.rsi_14) > 60 ? 'text-green' : 'text-slate'}">
                    {techSnapshot.rsi_14 !== undefined && techSnapshot.rsi_14 !== null ? Number(techSnapshot.rsi_14).toFixed(1) : '-'}
                  </span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">20 EMA</span>
                  <span class="pill-v">₹{techSnapshot.ema_20 != null ? Number(techSnapshot.ema_20).toFixed(2) : '-'}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">Volume Surge</span>
                  <span class="pill-v {Number(techSnapshot.volume_surge_ratio) >= 2.0 ? 'text-green font-bold' : ''}">
                    {techSnapshot.volume_surge_ratio !== undefined && techSnapshot.volume_surge_ratio !== null ? `${Number(techSnapshot.volume_surge_ratio).toFixed(2)}x` : '-'}
                  </span>
                </div>
              </div>
            {:else}
              <div class="empty-notice">No indicator snapshot captured for this event type.</div>
            {/if}
          </div>
        </div>

        <!-- Section 2: Pre-Trade Risk (RMS) Snapshot -->
        <div class="grid-section">
          <div class="section-title">
            <span>🛡️ Pre-Trade Risk & Allocation Audit</span>
            {#if event.client_ucc}
              <span class="ucc-tag">Client: {event.client_ucc}</span>
            {/if}
          </div>
          <div class="section-card">
            {#if riskSnapshot}
              <div class="metrics-grid">
                <div class="metric-pill">
                  <span class="pill-k">Client Multiplier</span>
                  <span class="pill-v text-purple font-bold">{riskSnapshot.client_multiplier ? `${riskSnapshot.client_multiplier}x` : '1.0x'}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">Available Margin</span>
                  <span class="pill-v text-green">₹{safeNumber(riskSnapshot.margin_available, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">Required Margin</span>
                  <span class="pill-v text-slate">₹{safeNumber(riskSnapshot.margin_required, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">Max Drawdown Limit</span>
                  <span class="pill-v text-red">₹{safeNumber(riskSnapshot.max_drawdown_limit, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div class="metric-pill">
                  <span class="pill-k">Day Realized P&L</span>
                  <span class="pill-v {safeNumber(riskSnapshot.current_day_pnl, 0) >= 0 ? 'text-green' : 'text-red'}">
                    ₹{safeNumber(riskSnapshot.current_day_pnl, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            {:else}
              <div class="empty-notice">No client-specific RMS metrics recorded for this macro signal.</div>
            {/if}
          </div>
        </div>

        <!-- Section 3: Execution, Multi-Leg & Slippage Details -->
        <div class="grid-section">
          <div class="section-title">
            <span>⚡ Order Execution & Multi-Leg Status</span>
          </div>
          <div class="section-card">
            <div class="details-table-grid">
              <div class="detail-item">
                <span class="d-label">Symbol / Scrip:</span>
                <span class="d-val font-bold">{event.symbol || '-'} {event.scrip_code ? `(${event.scrip_code})` : ''}</span>
              </div>
              <div class="detail-item">
                <span class="d-label">Side & Quantity:</span>
                <span class="d-val {event.side === 'BUY' ? 'text-green font-bold' : event.side === 'SELL' ? 'text-red font-bold' : ''}">
                  {event.side || '-'} {event.fill_qty || event.order_qty || 0} Qty
                </span>
              </div>
              <div class="detail-item">
                <span class="d-label">Order Price:</span>
                <span class="d-val">₹{event.order_price != null ? Number(event.order_price).toFixed(2) : '-'}</span>
              </div>
              <div class="detail-item">
                <span class="d-label">Fill Price:</span>
                <span class="d-val font-bold">₹{event.fill_price != null ? Number(event.fill_price).toFixed(2) : '-'}</span>
              </div>
              <div class="detail-item">
                <span class="d-label">Slippage:</span>
                <span class="d-val {Number(event.slippage_paise) > 0 ? 'text-orange' : 'text-green'}">
                  {event.slippage_paise != null ? `${Number(event.slippage_paise).toFixed(2)} paise` : '0.00 paise'}
                </span>
              </div>
              <div class="detail-item">
                <span class="d-label">Trailing SL Price:</span>
                <span class="d-val text-orange">
                  {event.trailing_sl_price != null ? `₹${Number(event.trailing_sl_price).toFixed(2)}` : 'Not Set'}
                </span>
              </div>
              <div class="detail-item">
                <span class="d-label">Leg Index:</span>
                <span class="d-val">
                  {event.leg_index !== undefined && event.leg_index !== null ? `Leg ${event.leg_index}` : 'Single Leg'}
                  {#if event.parent_order_tag}
                    <span class="parent-tag">(Parent: {event.parent_order_tag})</span>
                  {/if}
                </span>
              </div>
              <div class="detail-item">
                <span class="d-label">Exchange Order No:</span>
                <span class="d-val"><code>{event.exchange_order_no || 'Pending / N/A'}</code></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 4: Latency Waterfall Breakdown -->
        <div class="grid-section">
          <div class="section-title">
            <span>⏱️ Microsecond Latency Waterfall</span>
          </div>
          <div class="section-card">
            {#if latency}
              <div class="waterfall-container">
                <div class="waterfall-step">
                  <div class="step-num">1</div>
                  <div class="step-info">
                    <span class="step-title">Signal Trigger ➔ RMS Check</span>
                    <span class="step-desc">Zero-alloc risk gate evaluation</span>
                  </div>
                  <div class="step-time text-cyan font-bold">{latency.signal_to_rms_us || 0} µs</div>
                </div>
                <div class="waterfall-line"></div>
                <div class="waterfall-step">
                  <div class="step-num">2</div>
                  <div class="step-info">
                    <span class="step-title">RMS Approval ➔ Gateway Dispatch</span>
                    <span class="step-desc">BSE BOW Packet 2000 encoding</span>
                  </div>
                  <div class="step-time text-purple font-bold">{latency.rms_to_gateway_us || 0} µs</div>
                </div>
                <div class="waterfall-line"></div>
                <div class="waterfall-step">
                  <div class="step-num">3</div>
                  <div class="step-info">
                    <span class="step-title">Gateway Dispatch ➔ BSE Ack (2073)</span>
                    <span class="step-desc">Exchange network roundtrip latency</span>
                  </div>
                  <div class="step-time text-blue font-bold">{latency.exchange_ack_latency_ms != null ? `${Number(latency.exchange_ack_latency_ms).toFixed(2)} ms` : '-'}</div>
                </div>
              </div>
            {:else}
              <div class="empty-notice">Latency metrics are recorded on ORDER_DISPATCHED & ORDER_FILLED events.</div>
            {/if}
          </div>
        </div>

        <!-- Section 5: Regulatory Compliance & SEBI Audit Headers -->
        <div class="grid-section full-width">
          <div class="section-title">
            <span>🏛️ Regulatory Audit & SEBI Compliance Identifiers</span>
          </div>
          <div class="section-card">
            <div class="compliance-row">
              <div class="comp-col">
                <span class="comp-k">SEBI Algo Approval ID:</span>
                <span class="comp-v"><code>{event.algo_id || 'ALGO_SEBI_MOM_2026_091'}</code></span>
              </div>
              <div class="comp-col">
                <span class="comp-k">Exchange CTCL ID:</span>
                <span class="comp-v"><code>{event.ctcl_id || 'CTCL_BSE_9921'}</code></span>
              </div>
              <div class="comp-col">
                <span class="comp-k">Dealer Terminal ID:</span>
                <span class="comp-v"><code>{event.dealer_id || 'DLR_HO_01'}</code></span>
              </div>
              <div class="comp-col">
                <span class="comp-k">Source IP Address:</span>
                <span class="comp-v"><code>{event.ip_address || '127.0.0.1'}</code></span>
              </div>
              <div class="comp-col">
                <span class="comp-k">Correlation IDs:</span>
                <span class="comp-v">
                  Signal: <code>{event.signal_id || '-'}</code> | 
                  Allocation: <code>{event.allocation_id || '-'}</code> | 
                  OrderTag: <code>{event.order_tag || '-'}</code>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 6: Formatted Raw JSON Inspector -->
        <div class="grid-section full-width">
          <div class="section-title display-flex justify-content-space-between align-items-center">
            <span>📜 Canonical Event Payload JSON</span>
            <Button small outline color="blue" onClick={copyJson}>
              <Icon f7="doc_on_clipboard" size="12" /> &nbsp; {copySuccess ? 'Copied!' : 'Copy JSON'}
            </Button>
          </div>
          <div class="section-card json-viewer-card">
            <pre class="json-code"><code>{JSON.stringify(event, null, 2)}</code></pre>
          </div>
        </div>
      </div>
    </div>
  {/if}
  </Page>
</Popup>

<style>
  .audit-decision-popup {
    --f7-popup-tablet-width: 920px;
    --f7-popup-tablet-height: 88vh;
  }
  .decision-card-container {
    padding: 16px;
    background: #0f172a;
    color: #e2e8f0;
    min-height: calc(100vh - 60px);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  .decision-hero-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 14px 18px;
    margin-bottom: 16px;
  }
  .hero-badge-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  .event-badge {
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  .badge-green { background: #065f46; color: #34d399; }
  .badge-red { background: #7f1d1d; color: #f87171; }
  .badge-purple { background: #581c87; color: #c084fc; }
  .badge-blue { background: #1e3a8a; color: #60a5fa; }
  .badge-orange { background: #7c2d12; color: #fb923c; }
  .badge-gray { background: #334155; color: #94a3b8; }

  .env-badge {
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }
  .badge-sim { background: #ca8a04; color: #fef08a; }
  .badge-live { background: #047857; color: #a7f3d0; }
  .source-badge {
    padding: 3px 6px;
    background: #334155;
    color: #cbd5e1;
    border-radius: 4px;
    font-size: 10px;
    text-transform: uppercase;
  }
  .hero-id-row {
    font-size: 13px;
    display: flex;
    gap: 16px;
    align-items: center;
  }
  .hero-event-id code {
    color: #38bdf8;
    background: #0f172a;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #334155;
  }
  .hero-time {
    color: #94a3b8;
  }

  .decision-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .grid-section.full-width {
    grid-column: 1 / -1;
  }
  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .strategy-tag, .ucc-tag {
    background: #334155;
    color: #38bdf8;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    text-transform: none;
  }
  .section-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 12px 14px;
    min-height: 110px;
  }
  .trigger-reason-box {
    background: #0f172a;
    border-left: 3px solid #38bdf8;
    padding: 8px 12px;
    border-radius: 0 4px 4px 0;
    margin-bottom: 12px;
  }
  .trigger-label {
    display: block;
    font-size: 10px;
    color: #64748b;
    text-transform: uppercase;
  }
  .trigger-text {
    font-size: 13px;
    color: #e2e8f0;
    font-weight: 500;
  }

  .metrics-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .metric-pill {
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 6px 10px;
    flex: 1 1 calc(33% - 10px);
    display: flex;
    flex-direction: column;
  }
  .pill-k {
    font-size: 10px;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .pill-v {
    font-size: 14px;
    font-weight: 600;
    color: #f8fafc;
  }

  .details-table-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
  }
  .detail-item {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    border-bottom: 1px dashed #334155;
    padding-bottom: 4px;
  }
  .d-label {
    color: #94a3b8;
  }
  .d-val {
    color: #f8fafc;
  }
  .parent-tag {
    font-size: 10px;
    color: #94a3b8;
    margin-left: 4px;
  }

  .waterfall-container {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .waterfall-step {
    display: flex;
    align-items: center;
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 8px 12px;
  }
  .step-num {
    width: 22px;
    height: 22px;
    background: #38bdf8;
    color: #0f172a;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 11px;
    margin-right: 12px;
  }
  .step-info {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .step-title {
    font-size: 12px;
    font-weight: 600;
    color: #e2e8f0;
  }
  .step-desc {
    font-size: 10px;
    color: #64748b;
  }
  .step-time {
    font-size: 13px;
  }
  .waterfall-line {
    width: 2px;
    height: 6px;
    background: #334155;
    margin-left: 22px;
  }

  .compliance-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px 16px;
    font-size: 12px;
  }
  .comp-col {
    display: flex;
    flex-direction: column;
  }
  .comp-k {
    color: #64748b;
    font-size: 10px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .comp-v code {
    color: #38bdf8;
    background: #0f172a;
    padding: 2px 4px;
    border-radius: 4px;
  }

  .json-viewer-card {
    max-height: 220px;
    overflow-y: auto;
    background: #020617;
  }
  .json-code {
    margin: 0;
    font-size: 11px;
    font-family: "SFMono-Regular", Consolas, Menlo, monospace;
    color: #38bdf8;
  }

  .empty-notice {
    color: #64748b;
    font-size: 12px;
    font-style: italic;
    padding: 12px 0;
  }

  .text-green { color: #34d399 !important; }
  .text-red { color: #f87171 !important; }
  .text-blue { color: #38bdf8 !important; }
  .text-purple { color: #c084fc !important; }
  .text-orange { color: #fb923c !important; }
  .text-cyan { color: #22d3ee !important; }
  .text-slate { color: #94a3b8 !important; }
  .font-bold { font-weight: bold; }
</style>
